/**
 * Invoice Preview Modal
 * Shows invoice details before sending to client
 */

import React, { useState } from 'react';
import { formatCurrency, formatDate } from '../utils/formatters';
import { formatBillingPeriod } from '../utils/graduatedDiscount';
import './Modal.css';

const InvoicePreviewModal = ({ invoice, onClose, onSend }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    setError('');
    setLoading(true);

    try {
      await onSend(invoice.id);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to send invoice');
    } finally {
      setLoading(false);
    }
  };

  // Extract discount info if available
  const hasGraduatedDiscount = invoice.graduatedDiscount && invoice.graduatedDiscount.enabled;
  const baseRetainer = invoice.baseRetainer || 0;
  const graduatedDiscountAmount = hasGraduatedDiscount ? (baseRetainer - invoice.totalAmount) : 0;

  // Manual invoice discount
  const hasManualDiscount = invoice.discountPercent > 0 || invoice.discountAmount > 0;
  const manualDiscountAmount = invoice.discountAmount || 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invoice Preview</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-body">
          {/* Invoice Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '2rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid #e0e0e0',
          }}>
            <div>
              <h3 style={{ margin: '0 0 0.5rem', color: '#2c3e50' }}>
                Invoice {invoice.invoiceNumber}
              </h3>
              {invoice.billingPeriod && (
                <p style={{ margin: 0, color: '#666' }}>
                  Billing Period: {formatBillingPeriod(invoice.billingPeriod)}
                </p>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '500',
                background: invoice.status === 'draft' ? '#fff3e0' : invoice.status === 'paid' ? '#e8f5e9' : '#e3f2fd',
                color: invoice.status === 'draft' ? '#e65100' : invoice.status === 'paid' ? '#2e7d32' : '#1565c0',
              }}>
                {invoice.status.toUpperCase()}
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}>
            <h4 style={{ margin: '0 0 0.75rem', color: '#2c3e50', fontSize: '0.9rem' }}>
              Bill To
            </h4>
            <div style={{ fontSize: '1rem', fontWeight: '500' }}>{invoice.clientName}</div>
            <div style={{ color: '#666' }}>{invoice.clientEmail}</div>
          </div>

          {/* Dates */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                Issue Date
              </div>
              <div style={{ fontWeight: '500' }}>{formatDate(invoice.issueDate)}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                Due Date
              </div>
              <div style={{ fontWeight: '500' }}>{formatDate(invoice.dueDate)}</div>
            </div>
          </div>

          {/* Graduated Discount Banner */}
          {hasGraduatedDiscount && (
            <div style={{
              background: '#e8f5e9',
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              marginBottom: '1.5rem',
              borderLeft: '4px solid #4caf50',
            }}>
              <div style={{ fontWeight: 'bold', color: '#2e7d32', marginBottom: '0.25rem' }}>
                Year {invoice.graduatedDiscount.currentYear} Loyalty Discount Applied
              </div>
              <div style={{ fontSize: '0.9rem', color: '#555' }}>
                {invoice.graduatedDiscount.discountPercent}% discount on base retainer
              </div>
            </div>
          )}

          {/* Manual Discount Banner */}
          {hasManualDiscount && !hasGraduatedDiscount && (
            <div style={{
              background: '#e8f5e9',
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              marginBottom: '1.5rem',
              borderLeft: '4px solid #4caf50',
            }}>
              <div style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                {invoice.discountPercent}% Discount Applied
              </div>
            </div>
          )}

          {/* Line Items Table */}
          <div style={{ marginBottom: '1.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: '#666', fontSize: '0.85rem' }}>
                    Description
                  </th>
                  <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#666', fontSize: '0.85rem' }}>
                    Qty
                  </th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: '#666', fontSize: '0.85rem' }}>
                    Rate
                  </th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: '#666', fontSize: '0.85rem' }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {(invoice.lineItems || []).map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{item.description}</td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>{item.quantity || 1}</td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                      {formatCurrency(item.rate || item.unitPrice || item.amount, invoice.currency)}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: '500' }}>
                      {formatCurrency(item.amount, invoice.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderRadius: '8px',
          }}>
            {hasGraduatedDiscount && baseRetainer > 0 && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  color: '#666',
                }}>
                  <span>Base Retainer:</span>
                  <span>{formatCurrency(baseRetainer, invoice.currency)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  color: '#4caf50',
                }}>
                  <span>Discount ({invoice.graduatedDiscount.discountPercent}% off):</span>
                  <span>-{formatCurrency(graduatedDiscountAmount, invoice.currency)}</span>
                </div>
              </>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
            }}>
              <span>Subtotal:</span>
              <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
            </div>

            {/* Manual Discount Row */}
            {hasManualDiscount && !hasGraduatedDiscount && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
                color: '#4caf50',
              }}>
                <span>Discount ({invoice.discountPercent}% off):</span>
                <span>-{formatCurrency(manualDiscountAmount, invoice.currency)}</span>
              </div>
            )}

            {invoice.tax > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
              }}>
                <span>Tax:</span>
                <span>{formatCurrency(invoice.tax, invoice.currency)}</span>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: '0.75rem',
              borderTop: '2px solid #d0d0d0',
              marginTop: '0.5rem',
              fontWeight: 'bold',
              fontSize: '1.1rem',
            }}>
              <span>Total Due:</span>
              <span style={{ color: '#2c3e50' }}>
                {formatCurrency(invoice.totalAmount, invoice.currency)}
              </span>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#666', fontSize: '0.9rem' }}>
                Notes
              </h4>
              <p style={{ margin: 0, color: '#333', whiteSpace: 'pre-wrap' }}>
                {invoice.notes}
              </p>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSend}
            disabled={loading}
          >
            {loading ? 'Sending...' : '✉️ Send Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreviewModal;
